/**
 * ConversationItem - Item individual de conversa na lista
 * Reskin visual (Design System 2026-07-18 chat-redesign) — núcleo/lógica inalterado
 */

import React from 'react';
import { tagStyleFrom, useTagCatalog } from '@/shared/hooks/useTagCatalog';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import type { ConversationResponse } from '@troia-v3/types';
import { formatRelativeDate, getInitials } from '../utils/dateFormatter';
import { useAuth } from '@/contexts/AuthContext';
import { IntegrationIcon } from '@/shared/icons';
import { Icon } from '@/shared/icons';
import { useConversationDraft } from '../hooks/useConversationDraft';

interface ConversationItemProps {
  conversation: ConversationResponse;
  isSelected?: boolean;
  onClick?: () => void;
  expanded?: boolean;
  providerId?: string;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  onClick,
  expanded = true,
  providerId
}) => {
  const { state: authState } = useAuth();
  // Cor do ponto vem do CATÁLOGO (22/09/2026). Antes era um hash local sobre
  // uma paleta própria — dez cores diferentes das que o drawer de tags usava,
  // então a MESMA tag tinha uma cor na lista e outra ao abrir o contato.
  // Órfã cai no `pickTagColor`, o mesmo hash que o catálogo usa para atribuir
  // cor, então nenhum ponto fica cinza.
  const { data: tagCatalog } = useTagCatalog();
  // mockup: sino-mutado POR CONVERSA, discreto — o mute GLOBAL de som já é indicado
  // pelo botão da toolbar; repeti-lo em todos os itens vira ruído visual.
  const showMutedIcon = !!conversation.muted;
  // Selo de contato bloqueado (2026-09-01) — precisa existir nos DOIS estados
  // da sidebar (recolhida e expandida), senão some quando o usuário recolhe.
  const showBlockedIcon = conversation.contact?.blocked === true;

  // Conversa aberta não mostra indicador — o texto já está no composer.
  const draft = useConversationDraft(isSelected ? undefined : conversation.id);

  // ✅ Nome e avatar dinâmicos: contact.name para conversas 1:1, group.name para grupos
  const displayName = conversation.group?.name || conversation.contact?.name || conversation.subject || `Conversa #${conversation.id.slice(0, 8)}`;
  const avatarUrl = conversation.group?.picture || conversation.contact?.picture;

  // ✅ Arch 3.4: Calcular número de mensagens não lidas para o usuário atual
  const unreadCount = conversation.unreadTracking && authState.user?.id
    ? (conversation.unreadTracking[authState.user.id]?.count || 0)
    : 0;

  // ✅ Cor da borda lateral baseada no status (apenas chats individuais, não grupos)
  const isGroup = !!conversation.group;
  const getBorderColor = () => {
    if (isGroup) return 'var(--theme-sidebar-border)';
    switch (conversation.status) {
      case 'waiting':
        return 'var(--theme-warning-color)';  // Laranja
      case 'active':
        return 'var(--theme-success-color)';  // Verde
      case 'closed':
        return 'var(--theme-sidebar-border)'; // Cinza
      default:
        return 'var(--theme-sidebar-border)';
    }
  };

  return (
    <div
      data-testid={`conversation-item-${conversation.id}`}
      onClick={onClick}
      className={`cursor-pointer transition-colors ${!isGroup ? 'border-l-2' : ''} ${
        expanded
          ? 'px-2 py-2.5'
          : 'py-2 flex justify-center'
      }`}
      style={{
        ...(!isGroup ? { borderLeftColor: getBorderColor() } : {}),
        backgroundColor: isSelected ? 'var(--theme-sidebar-item-active-bg)' : 'transparent'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      <div className={`flex ${expanded ? 'items-start gap-2.5' : 'items-center'}`}>
        {/* Avatar Composto: Contato + Badge do Operador + Badge do Provider */}
        <div className="relative flex-shrink-0">
          {/* ✅ Avatar do Contato usando componente centralizado (fallback slate #64748b) */}
          <Avatar className={expanded ? 'w-11 h-11' : 'w-10 h-10'} isSquircle>
            <AvatarImage
              src={avatarUrl}
              alt={displayName}
              isSquircle
            />
            <AvatarFallback isSquircle>
              <span className={`font-semibold ${expanded ? 'text-sm' : 'text-xs'}`}>
                {getInitials(displayName)}
              </span>
            </AvatarFallback>
          </Avatar>

          {/* Badge do Assignee (Atendente) - Apenas para conversas individuais (não grupos) */}
          {conversation.assignee && expanded && !conversation.group && (
            <div
              className="absolute -top-1 -left-1 w-[18px] h-[18px] rounded-md overflow-hidden flex items-center justify-center"
              style={{
                backgroundColor: conversation.assignee.picture ? 'var(--theme-card-background)' : 'var(--theme-button-primary)',
                border: '1.5px solid var(--theme-card-background)',
                zIndex: 10
              }}
              title={`Atendente: ${conversation.assignee.name}`}
            >
              {conversation.assignee.picture ? (
                <img
                  src={conversation.assignee.picture}
                  alt={conversation.assignee.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white text-[8px] font-bold leading-none">
                  {getInitials(conversation.assignee.name)}
                </span>
              )}
            </div>
          )}

          {/* Badge do Provider - Expandida */}
          {providerId && expanded && (
            <div
              className="absolute -bottom-0.5 -right-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center overflow-hidden"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1.5px solid var(--theme-card-background)'
              }}
            >
              <IntegrationIcon id={providerId ?? 'unknown'} size={12} variant="brand" />
            </div>
          )}

          {/* Badge do Provider - Recolhida */}
          {providerId && !expanded && (
            <div
              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full overflow-hidden flex items-center justify-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1.5px solid var(--theme-card-background)'
              }}
            >
              <IntegrationIcon id={providerId ?? 'unknown'} size={9} variant="brand" />
            </div>
          )}

          {/* ✅ Badge de mensagens não lidas quando sidebar recolhida */}
          {!expanded && unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'var(--theme-button-primary)',
                border: '2px solid var(--theme-card-background)'
              }}
              title={`${unreadCount} mensagem${unreadCount > 1 ? 's' : ''} não lida${unreadCount > 1 ? 's' : ''}`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}

          {/* ✅ Indicador de silenciada — sidebar recolhida */}
          {!expanded && showMutedIcon && (
            <div
              className="absolute -bottom-0.5 -left-0.5 w-4 h-4 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1.5px solid var(--theme-card-background)',
              }}
              title="Silenciada"
              data-testid={`conversation-muted-collapsed-${conversation.id}`}
            >
              <Icon
                name="bell-off"
                className="w-2.5 h-2.5"
                style={{ color: 'var(--theme-muted-text)' }}
              />
            </div>
          )}

          {/* ✅ Indicador de contato bloqueado — sidebar recolhida. Canto superior
              esquerdo (R20): o inferior esquerdo já é da silenciada, o inferior
              direito é do canal/provider, e o superior direito é do contador de
              não lidas (R19 tentou ali primeiro e colidia com ele) — superior
              esquerdo é o único canto livre dos quatro. */}
          {!expanded && showBlockedIcon && (
            <div
              className="absolute -top-0.5 -left-0.5 w-4 h-4 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1.5px solid var(--theme-card-background)',
              }}
              title="Contato bloqueado"
              data-testid={`conversation-blocked-collapsed-${conversation.id}`}
            >
              <Icon name="slash" className="w-2.5 h-2.5" style={{ color: 'var(--theme-error-fg)' }} />
            </div>
          )}
        </div>

        {/* Content - 2 Linhas apenas */}
        {expanded && (
          <div className="flex-1 min-w-0">
            {/* Linha 1: Nome (Contact ou Group) + [ícone muted] + Data Relativa */}
            <div className="flex items-center gap-1.5">
              <h4
                className="font-semibold text-sm truncate flex-1 min-w-0"
                style={{ color: 'var(--theme-card-text)' }}
                data-testid={`conversation-name-${conversation.id}`}
              >
                {displayName}
              </h4>
              <span
                className="text-[11px] tabular-nums flex-shrink-0"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {formatRelativeDate(conversation.lastMessageAt || conversation.createdAt)}
              </span>
            </div>

            {/* Linha 2: Preview da última mensagem */}
            <div className="flex items-center gap-1.5 mt-0.5">
              {draft ? (
                <p
                  className="text-[13px] truncate flex-1 min-w-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  <span
                    className="font-semibold"
                    style={{ color: '#d97706' }}
                    data-testid={`conversation-draft-indicator-${conversation.id}`}
                  >
                    {'Rascunho: '}
                  </span>
                  {draft.text ?? 'anexo pendente'}
                </p>
              ) : (
                <p
                  className="text-[13px] truncate flex-1 min-w-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {conversation.lastMessage || (conversation.messageCount > 0 ? `${conversation.messageCount} mensagens` : 'Sem mensagens')}
                </p>
              )}

              {/* Tags do contato — dots coloridos discretos */}
              {conversation.contact?.tags && conversation.contact.tags.length > 0 && (
                <div
                  className="flex items-center gap-1 flex-shrink-0"
                  data-testid={`conversation-tags-${conversation.id}`}
                >
                  {conversation.contact.tags.slice(0, 3).map((tagName) => (
                    <span
                      key={tagName}
                      className="w-[7px] h-[7px] rounded-full"
                      style={{ backgroundColor: tagStyleFrom(tagCatalog, tagName).color }}
                      title={tagName}
                      data-testid={`conversation-tag-dot-${tagName}`}
                    />
                  ))}
                  {conversation.contact.tags.length > 3 && (
                    <span
                      className="text-[10px] font-medium leading-none"
                      style={{ color: 'var(--theme-muted-text)' }}
                      title={conversation.contact.tags.slice(3).join(', ')}
                    >
                      +{conversation.contact.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* ✅ Arch 3.4: Badge com contador de mensagens não lidas */}
              {unreadCount > 0 && (
                <span
                  className="flex-shrink-0 min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full flex items-center justify-center leading-none"
                  style={{
                    backgroundColor: 'var(--theme-button-primary)',
                    color: 'var(--theme-button-primary-text)'
                  }}
                  title={`${unreadCount} mensagem${unreadCount > 1 ? 's' : ''} não lida${unreadCount > 1 ? 's' : ''}`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}

              {/* Silenciada — slot direito da linha 2 (anatomia do mockup) */}
              {showMutedIcon && (
                <Icon
                  name="bell-off"
                  className="w-3.5 h-3.5 flex-shrink-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                  data-testid={`conversation-muted-${conversation.id}`}
                />
              )}

              {/* Contato bloqueado — slot direito da linha 2, junto do de silenciada.
                  `Icon` não repassa data-testid ao DOM — o testid vai no wrapper. */}
              {showBlockedIcon && (
                <span data-testid={`conversation-blocked-${conversation.id}`} className="flex-shrink-0">
                  <Icon
                    name="slash"
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--theme-error-fg)' }}
                  />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
